import { Button } from "@/components/ui/button";
import { fmt } from "@/hooks/useHomeBuyingSim";
import ModuleFeedbackCard from "@/components/ai/ModuleFeedbackCard";
import type { AnyRow } from "@/lib/rowTypes";

interface Props {
  sim: {
    selectedHome: AnyRow;
    downPaymentPct: number;
    downPaymentAmount: number;
    closingCosts: { total: number };
    cashToClose: number;
    loanAmount: number;
    mortgageYears: number;
    rate: number;
    monthlyPI: number;
    totalMonthlyHousing: number;
    profile: { checking: number; savings: number; emergencyFund: number };
    affordabilityRating: string | null;
    processing: boolean;
  };
  onFinish: () => void;
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <p className="text-xs font-heading font-semibold text-muted-foreground uppercase tracking-wider mb-2">{children}</p>;
}

function Row({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`text-sm font-semibold ${highlight ? 'text-primary' : 'text-foreground'} capitalize`}>{value}</span>
    </div>
  );
}

export default function FinalSummaryScreen({ sim, onFinish }: Props) {
  const checkingUsed = Math.min(sim.profile.checking, sim.cashToClose);
  const savingsUsed = Math.max(0, sim.cashToClose - sim.profile.checking);

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="font-heading text-lg font-bold text-foreground">Purchase Summary</h2>
        <p className="text-sm text-muted-foreground">Review your complete home purchase details before finalizing.</p>
      </div>

      <div className="glass-card p-4 rounded-xl space-y-2">
        <SectionTitle>Home</SectionTitle>
        <Row label="Home" value={sim.selectedHome?.home_name || ''} />
        <Row label="Price" value={fmt(sim.selectedHome?.home_price || 0)} />
      </div>

      <div className="glass-card p-4 rounded-xl space-y-2">
        <SectionTitle>Down Payment & Closing</SectionTitle>
        <Row label={`Down Payment (${Math.round(sim.downPaymentPct * 100)}%)`} value={fmt(sim.downPaymentAmount)} />
        <Row label="Closing Costs" value={fmt(sim.closingCosts.total)} />
        <div className="border-t border-border pt-2 mt-2">
          <Row label="Cash Needed to Close" value={fmt(sim.cashToClose)} highlight />
        </div>
      </div>

      <div className="glass-card p-4 rounded-xl space-y-2">
        <SectionTitle>Mortgage</SectionTitle>
        <Row label="Loan Amount" value={fmt(sim.loanAmount)} />
        <Row label="Mortgage Term" value={`${sim.mortgageYears}-Year Fixed`} />
        <Row label="Interest Rate" value={`${(sim.rate * 100).toFixed(1)}%`} />
        <Row label="Monthly P&I" value={fmt(sim.monthlyPI)} />
        <Row label="Property Taxes" value={fmt(sim.selectedHome?.property_tax_monthly || 0)} />
        <Row label="Insurance" value={fmt(sim.selectedHome?.insurance_monthly || 0)} />
        <div className="border-t border-border pt-2 mt-2">
          <Row label="Total Monthly Housing" value={fmt(sim.totalMonthlyHousing)} highlight />
        </div>
      </div>

      <div className="glass-card p-4 rounded-xl space-y-2">
        <SectionTitle>After Purchase</SectionTitle>
        <Row label="Remaining Checking" value={fmt(Math.max(0, sim.profile.checking - checkingUsed))} />
        <Row label="Remaining Savings" value={fmt(Math.max(0, sim.profile.savings - savingsUsed))} />
        <Row label="Emergency Fund" value={fmt(sim.profile.emergencyFund)} />
        <Row label="Affordability" value={sim.affordabilityRating || 'N/A'} highlight />
      </div>

      <p className="text-sm text-muted-foreground text-center">
        You completed your home purchase simulation. This home will be added as an asset in your HeirWise ecosystem.
      </p>

      <ModuleFeedbackCard
        moduleName="Home Buying Simulator"
        moduleKey="home_buying_sim"
        finalMetrics={{
          home_name: sim.selectedHome?.home_name,
          home_price: sim.selectedHome?.home_price,
          down_payment_pct: sim.downPaymentPct,
          loan_amount: sim.loanAmount,
          monthly_housing: sim.totalMonthlyHousing,
          affordability_rating: sim.affordabilityRating,
          cash_to_close: sim.cashToClose,
        }}
        outcome={sim.affordabilityRating === "Comfortable" || sim.affordabilityRating === "Manageable" ? "strong" : "stretched"}
      />

      <Button onClick={onFinish} disabled={sim.processing} className="w-full">
        {sim.processing ? 'Processing...' : 'Finish Module'}
      </Button>
    </div>
  );
}
